/**
 * Pure color-theme types and palettes.
 * DOM application lives in ui/styles/theme.ts. The renderer may import this
 * leaf module, but not the UI installer.
 */
import { darkenHex, darkenPalette, darkenRgbNum } from './color.js';

export type ColorThemePreference = 'light' | 'dark' | 'system';
export type ResolvedColorTheme = 'light' | 'dark';

/** RGB scale applied to puzzle colors when dark mode and palette darkening are both on. */
export const DARK_PALETTE_DARKEN_AMOUNT = 0.4;

/** X-mark stroke on crossed cells. Darkened with the puzzle palette. */
export const CELL_CROSS_COLOR = 0x888888;
export const CELL_CROSS_HEX = '#888888';
/** Brief fill behind the cross pop animation. */
export const CELL_CROSS_FILL_COLOR = 0xdddddd;

export const THEME_COLOR_META: Record<ResolvedColorTheme, string> = {
  light: '#ffffff',
  dark: '#141414',
};

export interface ChromeTheme {
  canvasBackground: number;
  headerBackgroundCss: string;
  footerBackground: number;
  footerBorder: number;
  footerLabel: number;
  cornerBackgroundCss: string;
  footerCornerBackgroundCss: string;
  footerCornerBorderCss: string;
  minimapViewportBorderCss: string;
  gridThin: number;
  gridThick: number;
  cellCross: number;
  cellCrossFill: number;
}

// Poster style: the board sits on the page color, clue strips are the muted
// block color, and the thick gridlines and borders are ink.
export const CHROME_THEMES: Record<ResolvedColorTheme, ChromeTheme> = {
  light: {
    canvasBackground: 0xffffff,
    headerBackgroundCss: '#f1f1f1',
    footerBackground: 0xf1f1f1,
    footerBorder: 0x111111,
    footerLabel: 0x4a4a4a,
    cornerBackgroundCss: '#f1f1f1',
    footerCornerBackgroundCss: '#f1f1f1',
    footerCornerBorderCss: '2px solid #111111',
    minimapViewportBorderCss: '2px solid #3a86ff',
    gridThin: 0xa6a6a6,
    gridThick: 0x111111,
    cellCross: CELL_CROSS_COLOR,
    cellCrossFill: CELL_CROSS_FILL_COLOR,
  },
  dark: {
    canvasBackground: 0x141414,
    headerBackgroundCss: '#2a2a2a',
    footerBackground: 0x2a2a2a,
    footerBorder: 0xb9b9b9,
    footerLabel: 0xb9b9b9,
    cornerBackgroundCss: '#1f1f1f',
    footerCornerBackgroundCss: '#2a2a2a',
    footerCornerBorderCss: '2px solid #b9b9b9',
    minimapViewportBorderCss: '2px solid #3a86ff',
    // Darkened empty cells are #999999 — keep both grid weights below that.
    gridThin: 0x555555,
    gridThick: 0x111111,
    cellCross: CELL_CROSS_COLOR,
    cellCrossFill: CELL_CROSS_FILL_COLOR,
  },
};

export function resolveColorTheme(
  preference: ColorThemePreference,
  prefersDark = false,
): ResolvedColorTheme {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

export function shouldDarkenPuzzleColors(
  theme: ResolvedColorTheme,
  darkenPuzzleColors: boolean,
): boolean {
  return theme === 'dark' && darkenPuzzleColors;
}

export function themeRgbColor(
  color: number,
  theme: ResolvedColorTheme,
  darkenPuzzleColors = false,
): number {
  return shouldDarkenPuzzleColors(theme, darkenPuzzleColors)
    ? darkenRgbNum(color, DARK_PALETTE_DARKEN_AMOUNT)
    : color;
}

export function themeHexColor(
  hex: string,
  theme: ResolvedColorTheme,
  darkenPuzzleColors = false,
): string {
  return shouldDarkenPuzzleColors(theme, darkenPuzzleColors)
    ? darkenHex(hex, DARK_PALETTE_DARKEN_AMOUNT)
    : hex;
}

export function themePuzzleColors(
  colors: string[],
  theme: ResolvedColorTheme,
  darkenPuzzleColors = false,
): string[] {
  return shouldDarkenPuzzleColors(theme, darkenPuzzleColors)
    ? darkenPalette(colors, DARK_PALETTE_DARKEN_AMOUNT)
    : colors;
}

export function withThemedCellCrosses(
  chrome: ChromeTheme,
  theme: ResolvedColorTheme,
  darkenPuzzleColors = false,
): ChromeTheme {
  const cellCross = themeRgbColor(CELL_CROSS_COLOR, theme, darkenPuzzleColors);
  const cellCrossFill = themeRgbColor(CELL_CROSS_FILL_COLOR, theme, darkenPuzzleColors);
  if (cellCross === chrome.cellCross && cellCrossFill === chrome.cellCrossFill) return chrome;
  return { ...chrome, cellCross, cellCrossFill };
}

export function themedCellCrossHex(
  theme: ResolvedColorTheme,
  darkenPuzzleColors = false,
): string {
  return themeHexColor(CELL_CROSS_HEX, theme, darkenPuzzleColors);
}
